import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const field = "bg-white/5 border-white/10";

export const apiError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || "").filter(Boolean).join(" ");
  return "A apărut o eroare. Încercați din nou.";
};

export const startGoogleLogin = () => {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/account";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

function GoogleButton() {
  return (
    <button type="button" onClick={startGoogleLogin} data-testid="btn-google-login"
      className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 font-semibold rounded-md h-11 hover:bg-gray-100 transition-colors">
      <svg viewBox="0 0 48 48" className="w-5 h-5" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
      Continuă cu Google
    </button>
  );
}

const LabeledInput = ({ label, testid, ...props }) => (
  <div>
    <Label className="text-xs text-gray-400">{label}</Label>
    <Input data-testid={testid} className={field} {...props} />
  </div>
);

export function CustomerAuthForm({ onLoggedIn }) {
  const [mode, setMode] = useState("login");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = isRegister ? form : { email: form.email, password: form.password };
      const resp = await fetch(`${API}/api/public/customer/${isRegister ? "register" : "login"}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(apiError(data.detail));
      if (isRegister) toast.success("Cont creat cu succes!");
      onLoggedIn(data);
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  return (
    <div className="max-w-sm mx-auto">
      <div className="text-center mb-6">
        <User className="w-12 h-12 text-[#FF0033] mx-auto mb-3" />
        <h1 className="text-2xl font-bold" data-testid="account-title">{isRegister ? "Creează cont" : "Contul meu"}</h1>
        <p className="text-gray-400 text-sm mt-1">Urmărește comenzile și rezervările tale</p>
      </div>
      <GoogleButton />
      <div className="flex items-center gap-3 my-5 text-xs text-gray-500"><div className="flex-1 h-px bg-white/10" />sau cu email<div className="flex-1 h-px bg-white/10" /></div>
      <form onSubmit={submit} className="space-y-3" data-testid={`customer-${mode}-form`}>
        {isRegister && (
          <div className="grid grid-cols-2 gap-3">
            <LabeledInput label="Prenume *" testid="reg-first-name" value={form.first_name} onChange={set("first_name")} required />
            <LabeledInput label="Nume" testid="reg-last-name" value={form.last_name} onChange={set("last_name")} />
          </div>
        )}
        <LabeledInput label="Email *" testid="customer-email" type="email" autoComplete="email" value={form.email} onChange={set("email")} required />
        {isRegister && <LabeledInput label="Telefon * (același ca în comenzi)" testid="reg-phone" type="tel" value={form.phone} onChange={set("phone")} required />}
        <LabeledInput label="Parolă *" testid="customer-password" type="password" autoComplete={isRegister ? "new-password" : "current-password"} minLength={6} value={form.password} onChange={set("password")} required />
        <Button type="submit" disabled={loading} className="w-full bg-[#FF0033] hover:bg-[#e0002d] text-white" data-testid={isRegister ? "btn-register" : "btn-login-customer"}>
          {loading ? "..." : isRegister ? "Creează cont" : "Conectează-te"}
        </Button>
        <p className="text-center text-sm text-gray-400">
          {isRegister ? "Ai deja cont? " : "Nu ai cont? "}
          <button type="button" data-testid="toggle-auth-mode" onClick={() => setMode(isRegister ? "login" : "register")} className="text-[#FF0033] hover:underline">
            {isRegister ? "Conectează-te" : "Înregistrează-te"}
          </button>
        </p>
      </form>
    </div>
  );
}
